import * as React from 'react';
import styled from 'styled-components';
import CreatableSelect from 'react-select/creatable';
import Select, {
  ControlProps,
  CSSObjectWithLabel,
  MenuPlacement,
  MenuPosition,
  OptionProps,
} from 'react-select';
import { DropdownIcon } from './images/DropdownIcon';
import { theme } from './style';
import { useCovideoTheme } from 'react-covideo-common';

export type Option = {
  value: string | number;
  label: string | number;
};

type Props = {
  value: any;
  defaultValue?: any;
  options: Array<Option> | any;
  placeholder?: string;
  isLoading?: boolean;
  name?: string;
  className?: string;
  dropdownHeight?: string | number;
  height?: number;
  width?: number;
  disabled?: boolean;
  zIndexProp?: number;
  creatable?: boolean;
  error?: boolean;
  inputId?: any;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: MenuPosition;
  menuPlacement?: MenuPlacement;
  ariaLabel?: string;
  onMenuScrollToBottom?: () => void;
  onMenuClose?: () => void;
  onChange: (value: any, actionMeta?: any) => void;
  onInputChange?: (inputValue: any, actionMeta?: any) => void;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => string;
  extendStyles?: {
    container?: React.CSSProperties;
    menu?: React.CSSProperties;
    control?: React.CSSProperties;
    menuList?: React.CSSProperties;
    valueContainer?: React.CSSProperties;
    input?: React.CSSProperties;
  };
  showIndicator?: boolean;
  chipValueSelected?: boolean;
  isClearable?: boolean;
  isMulti?: any /* used as any because creatable select not considering it as boolean */;
  onCreateOption?: (e?: any) => void;
  isShareVideoModal?: boolean;
  isSearchable?: boolean;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  menuShouldBlockScroll?: boolean;
  minMenuHeight?: number;
  closeMenuOnScroll?: boolean | ((event: Event) => boolean) | undefined;
  filterOption?:
    | ((option: Option, rawInput: string) => boolean)
    | null
    | undefined;
  formatOptionLabel?:
    | ((option: any, labelMeta: any) => React.ReactNode)
    | undefined;
  customComponents?: any;
};

type DropdownArrowProps = {
  menuisopen?: boolean;
};

const DropdownArrowIcon = styled.span<DropdownArrowProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding-right: 12px;
  position: relative;
  svg {
    ${(props) => props.menuisopen && 'transform: rotate(180deg);'}
  }
`;

const getControlStyles = (props: any) => {
  const { isDisabled, menuIsOpen, selectProps } = props;

  if (isDisabled) {
    return { borderColor: theme.palette.transparent };
  }
  if (selectProps.error) {
    return { borderColor: theme.palette.error };
  }
  if (menuIsOpen) {
    return { borderColor: theme.palette.covideoBlue100 };
  }
  return { borderColor: theme.palette.covideoGray40 };
};

const getOptionStyles = (
  props: OptionProps<Option, false>,
  isShareVideoModal?: boolean /* SUS-1302 changes */
) => {
  const { isFocused, isSelected, isDisabled } = props;
  const covideoTheme = useCovideoTheme();
  if (isDisabled) {
    return {
      color: theme.palette.gray40,
      backgroundColor: theme.palette.gray10,
      cursor: 'not-allowed',
    };
  }
  if (isFocused || isSelected) {
    /* SUS-1302 changes */
    if (isShareVideoModal) {
      return {
        color: theme.palette.black_1_100,
        backgroundColor: 'rgb(222, 235, 255)',
      };
    }
    /*****/
    return {
      color: covideoTheme.colors.primary[100],
      backgroundColor: theme.palette.blue05,
    };
  }

  return {
    color: theme.palette.covideoGray100,
    backgroundColor: theme.palette.white,
  };
};

export const Dropdown = (props: Props) => {
  const {
    ariaLabel = 'Aria label is missing',
    name,
    options,
    value,
    defaultValue = undefined,
    placeholder = 'No Folders',
    onChange,
    getOptionLabel,
    getOptionValue,
    menuPosition = 'absolute',
    menuPlacement = 'auto',
    menuPortalTarget,
    inputId,
    className,
    dropdownHeight = 'auto',
    height = 48,
    width,
    disabled = false,
    creatable = true,
    error = false,
    onMenuScrollToBottom,
    onMenuClose,
    extendStyles,
    showIndicator = true,
    isClearable = false,
    onInputChange /* SUS-1302 changes */,
    onCreateOption /* SUS-1302 changes */,
    isMulti = false /* SUS-1302 changes */,
    isShareVideoModal = false /* SUS-1302 changes */,
    zIndexProp = 9999,
    isSearchable,
    formatCreateLabel,
    closeMenuOnScroll = undefined,
    isLoading = false,
    filterOption,
    formatOptionLabel,
    customComponents,
  } = props;

  //common props
  const commonProps = {
    error,
    options,
    className,
    name,
    placeholder,
    value,
    defaultValue,
    getOptionLabel,
    getOptionValue,
    onChange,
    menuPlacement,
    menuPosition,
    isDisabled: disabled,
    menuPortalTarget,
    'aria-label': ariaLabel,
    onMenuScrollToBottom,
    onMenuClose,
    isClearable,
    onInputChange,
    onCreateOption /* SUS-1302 changes */,
    isMulti /* SUS-1302 changes */,
    isSearchable,
    filterOption,
  };

  const DropdownIndicator = (props: any) => {
    return showIndicator ? (
      <DropdownArrowIcon menuisopen={props.selectProps.menuisopen}>
        <DropdownIcon width={'12px'} height={'12px'} opacity={0.5} />
      </DropdownArrowIcon>
    ) : (
      <></>
    );
  };

  //dropdown styles
  const styles = {
    container: (base: CSSObjectWithLabel) => {
      return {
        ...base,
        ...((extendStyles && extendStyles.container) || {}),
      };
    },

    control: (
      base: CSSObjectWithLabel,
      props: ControlProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        width: width,
        boxShadow: 'none',
        borderRadius: 6,
        minHeight: '40px',
        cursor: 'pointer',
        ...getControlStyles(props),
        ...((extendStyles && extendStyles.control) || {}),
        //@ts-ignore
        '&:hover': {
          borderColor: isShareVideoModal
            ? 'rgb(128, 189, 255)'
            : theme.palette.covideoBlue100 /* SUS-1302 changes */,
        },
        '&:focus-within': {
          borderColor: isShareVideoModal
            ? 'rgb(128, 189, 255)'
            : theme.palette.covideoBlue100 /* SUS-1302 changes */,
        },
      };
    },
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      width: '95%',
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      ...((extendStyles && extendStyles.valueContainer) || {}),
    }),
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      ...((extendStyles && extendStyles.input) || {}),
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: zIndexProp || 9999,
    }),
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      ...((extendStyles && extendStyles.menuList) || {}),
      height: dropdownHeight,
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      ...((extendStyles && extendStyles.menu) || {}),
      marginTop: 2,
      color: theme.palette.primaryDarkBlue,
    }),
    option: (
      base: CSSObjectWithLabel,
      props: OptionProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        height: isShareVideoModal ? 35 : height /* SUS-1302 changes */,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        cursor: 'pointer',
        fontFamily: 'Work Sans',
        transition: 'all 0.3s',
        fontWeight: isShareVideoModal ? 'normal' : 600 /* SUS-1302 changes */,
        fontSize: 15,
        zIndex: 1,
        ...getOptionStyles(props, isShareVideoModal) /* SUS-1302 changes */,
      };
    },
  };

  if (creatable) {
    return (
      <CreatableSelect
        {...commonProps}
        isLoading={false}
        components={{
          DropdownIndicator: DropdownIndicator,
          IndicatorSeparator: () => null,
          ...customComponents,
        }}
        styles={styles}
        isSearchable={isSearchable}
        formatCreateLabel={formatCreateLabel}
        formatOptionLabel={formatOptionLabel}
      />
    );
  }

  return (
    <Select
      {...commonProps}
      inputId={inputId}
      maxMenuHeight={220}
      menuShouldScrollIntoView={false}
      menuPortalTarget={menuPortalTarget}
      isLoading={isLoading}
      components={{
        DropdownIndicator: DropdownIndicator,
        IndicatorSeparator: () => null,
        ...customComponents,
      }}
      styles={styles}
      isSearchable={isSearchable}
      closeMenuOnScroll={closeMenuOnScroll}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
    />
  );
};
